import ta from 'thinkingdata-browser'
import { getActLog } from '@/plugins/actlog'
import store from '@/store'

const PAGE_SOURCE_KEY = 'mp_page_source'
const PAGE_ENTER_AT_KEY = 'mp_page_enter_at'
const PAGE_EXIT_SENT_KEY = 'mp_page_exit_sent'

export const PAGE_SOURCE_IN = 1
export const PAGE_SOURCE_OUT = 2

export const DICE_NORMAL = 1
export const DICE_UNIVERSAL = 2

export const CASTLE_CELL_INDEX = 10

/** 端内 tokenHash 进页时写入，供清参后仍识别 page_source */
export const markPageSourceFromQuery = (query = {}) => {
  if (query?.tokenHash) {
    sessionStorage.setItem(PAGE_SOURCE_KEY, String(PAGE_SOURCE_IN))
  }
}

export const getPageSource = () => {
  const cached = Number(sessionStorage.getItem(PAGE_SOURCE_KEY))
  if (cached === PAGE_SOURCE_IN || cached === PAGE_SOURCE_OUT) return cached
  return PAGE_SOURCE_OUT
}

export const markPageEnter = () => {
  sessionStorage.setItem(PAGE_ENTER_AT_KEY, String(Date.now()))
  sessionStorage.removeItem(PAGE_EXIT_SENT_KEY)
}

export const getStayDurationSec = () => {
  const enterAt = Number(sessionStorage.getItem(PAGE_ENTER_AT_KEY) || 0)
  if (!enterAt) return 0
  return Math.max(0, Math.floor((Date.now() - enterAt) / 1000))
}

const track = (event, properties = {}) => {
  try {
    getActLog()?.track(event, properties)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[monopoly track]', event, e)
  }
}

export const loginTaAccount = (uid) => {
  const id = uid || store.state.actUserInfo?.uid || ''
  if (!id) return
  try {
    ta.login(id)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[monopoly ta.login]', e)
  }
}

export const toTrackRewards = (list) => {
  const arr = (Array.isArray(list) ? list : [])
    .map((item) => ({
      id: String(item?.propId || item?.id || ''),
      count: Number(item?.propCount ?? item?.count ?? 0),
    }))
    .filter((item) => item.id)
  return JSON.stringify(arr)
}

/** cell_type code：城堡格单独 castle；其余按 kind + moveSteps */
export const toCellTypeCode = (cellIndex, cellKind, moveSteps = 0) => {
  if (Number(cellIndex) === CASTLE_CELL_INDEX) return 'castle'
  const kind = String(cellKind || '')
  const steps = Number(moveSteps) || 0
  if (kind === 'forward' && steps > 0) return `forward_${steps}`
  if (kind === 'back' && steps > 0) return `back_${steps}`
  if (kind === 'start' || kind === 'normal' || kind === 'lucky' || kind === 'mystery') {
    return kind
  }
  return kind || 'unknown'
}

export const resolveCellTypeFromConfig = (commonConfig, cellIndex, fallbackKind = '') => {
  const cells = commonConfig?.boardCells || []
  const cell = cells.find((c) => Number(c?.cellIndex) === Number(cellIndex))
  if (!cell) {
    return toCellTypeCode(cellIndex, fallbackKind, 0)
  }
  return toCellTypeCode(cell.cellIndex, cell.kind || fallbackKind, cell.moveSteps)
}

export const trackPage = () => {
  markPageEnter()
  track('act_monopoly_page', { page_source: getPageSource() })
}

export const trackLogin = (pageSource = getPageSource()) => {
  loginTaAccount()
  track('act_monopoly_login', { page_source: pageSource })
}

export const trackPageExit = () => {
  if (sessionStorage.getItem(PAGE_EXIT_SENT_KEY) === '1') return
  sessionStorage.setItem(PAGE_EXIT_SENT_KEY, '1')
  track('monopoly_page_exit', { stay_duration: getStayDurationSec() })
}

export const trackRollDice = ({ diceType, rollNumber, diceRemain }) => {
  track('monopoly_roll_dice', {
    dice_type: Number(diceType),
    roll_number: Number(rollNumber),
    dice_remain: Number(diceRemain),
  })
}

export const trackRollAll = ({ diceCount, diceRemain }) => {
  track('monopoly_roll_all', {
    dice_count: Number(diceCount),
    dice_remain: Number(diceRemain),
  })
}

export const trackLandCell = ({ cellType, cellPosition, lapCount }) => {
  track('monopoly_land_cell', {
    cell_type: String(cellType || ''),
    cell_position: Number(cellPosition),
    lap_count: Number(lapCount),
  })
}

export const trackCellReward = ({ rewardDetail, cellType }) => {
  track('monopoly_cell_reward', {
    reward_detail: rewardDetail,
    cell_type: String(cellType || ''),
  })
}

export const trackGetDice = ({ source, diceAmount, diceType }) => {
  track('monopoly_get_dice', {
    source: Number(source),
    dice_amount: Number(diceAmount),
    dice_type: Number(diceType),
  })
}

export const trackLapComplete = ({ lapCount, completeTime }) => {
  track('monopoly_lap_complete', {
    lap_count: Number(lapCount),
    complete_time: Number(completeTime || Math.floor(Date.now() / 1000)),
  })
}

export const trackLapReward = ({ rewardDetail, milestoneLap }) => {
  track('monopoly_lap_reward', {
    reward_detail: rewardDetail,
    milestone_lap: Number(milestoneLap),
  })
}

export const trackClaimRewards = ({ rewardCount }) => {
  track('monopoly_claim_rewards', {
    reward_count: Number(rewardCount),
  })
}

/** 根据单次/多次 roll results 上报落地、奖励、过圈 */
export const trackRollResults = (results, commonConfig) => {
  const list = results || []
  list.forEach((result) => {
    // land_cell：按骰子先落点（含 forward/back/castle）；cell_reward：按最终停格（发奖依据）
    const landPosition = typeof result.landPosition === 'number'
      ? result.landPosition
      : result.finalPosition
    const finalPosition = typeof result.finalPosition === 'number'
      ? result.finalPosition
      : result.landPosition

    const landCellType = resolveCellTypeFromConfig(
      commonConfig,
      landPosition,
      '',
    )
    const finalCellType = resolveCellTypeFromConfig(
      commonConfig,
      finalPosition,
      result.cellKind,
    )
    const lapCount = Number(result.totalLaps ?? 0)

    if (typeof landPosition === 'number') {
      trackLandCell({
        cellType: landCellType,
        cellPosition: landPosition,
        lapCount,
      })
    }

    if (result.rewards?.length) {
      trackCellReward({
        rewardDetail: toTrackRewards(result.rewards),
        cellType: finalCellType,
      })
    }

    if (result.crossedLap) {
      trackLapComplete({ lapCount })
    }
  })
}
